import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useStaffRoles } from '@/hooks/useStaffRoles';

interface ImpersonationState {
  isImpersonating: boolean;
  staffUserId: string | null;
  targetUserId: string | null;
  targetUserInfo: {
    email: string;
    displayName: string;
  } | null;
}

interface ImpersonationContextType {
  impersonationState: ImpersonationState;
  hasActiveSession: boolean;
  startImpersonation: (targetUserId: string) => Promise<{ success: boolean; error?: string; magicLink?: string }>;
  endImpersonation: () => Promise<{ success: boolean; error?: string }>;
  getActiveMagicLink: () => Promise<{ success: boolean; error?: string; magicLink?: string }>;
  checkImpersonationStatus: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationContextType | null>(null);

export const useImpersonation = () => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within an ImpersonationProvider');
  }
  return context;
};

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session } = useAuth();
  const { isSupport, isSuperAdmin } = useStaffRoles();
  
  const [impersonationState, setImpersonationState] = useState<ImpersonationState>({
    isImpersonating: false,
    staffUserId: null,
    targetUserId: null,
    targetUserInfo: null,
  });

  const [hasActiveSession, setHasActiveSession] = useState(false);

  // Check for impersonation token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const impersonationToken = urlParams.get('impersonation_token');
    
    if (impersonationToken) {
      verifyImpersonationToken(impersonationToken);
      // Clean up URL
      const url = new URL(window.location.href);
      url.searchParams.delete('impersonation_token');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Check impersonation status when user changes
  useEffect(() => {
    if (user) {
      checkImpersonationStatus();
    } else {
      setImpersonationState({
        isImpersonating: false,
        staffUserId: null,
        targetUserId: null,
        targetUserInfo: null,
      });
    }
  }, [user?.id]);

  const verifyImpersonationToken = async (token: string) => {
    try {
      const response = await supabase.functions.invoke('user-impersonation', {
        body: {
          action: 'verify',
          token: token
        }
      });

      if (response.data?.success) {
        const sessionData = response.data.session;
        setImpersonationState({
          isImpersonating: true,
          staffUserId: sessionData.staffUserId,
          targetUserId: sessionData.targetUserId,
          targetUserInfo: {
            email: sessionData.targetUser.email,
            displayName: sessionData.targetUser.display_name
          }
        });
        
        // Store in sessionStorage for persistence across page reloads
        sessionStorage.setItem('impersonation_state', JSON.stringify({
          isImpersonating: true,
          staffUserId: sessionData.staffUserId,
          targetUserId: sessionData.targetUserId,
          targetUserInfo: {
            email: sessionData.targetUser.email,
            displayName: sessionData.targetUser.display_name
          }
        }));
      }
    } catch (error) {
      console.error('Failed to verify impersonation token:', error);
    }
  };

  const checkImpersonationStatus = async () => {
    if (!user || (!isSupport && !isSuperAdmin)) {
      setHasActiveSession(false);
      return;
    }

    // Check sessionStorage for existing impersonation state
    const stored = sessionStorage.getItem('impersonation_state');
    if (stored) {
      try {
        const parsedState = JSON.parse(stored);
        setImpersonationState(parsedState);
        setHasActiveSession(parsedState.isImpersonating);
        return;
      } catch (error) {
        console.error('Failed to parse stored impersonation state:', error);
        sessionStorage.removeItem('impersonation_state');
      }
    }

    // Check for active sessions for staff members
    try {
      const { data: sessions, error } = await supabase
        .from('impersonation_sessions')
        .select(`
          *,
          target_profile:profiles!target_user_id(email, display_name)
        `)
        .eq('staff_user_id', user.id)
        .eq('is_active', true)
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Error checking impersonation sessions:', error);
        setHasActiveSession(false);
        return;
      }

      if (sessions && sessions.length > 0) {
        const session = sessions[0];
        const targetProfile = session.target_profile;

        const newState = {
          isImpersonating: false, // This is for when the staff is not actually in impersonation mode
          staffUserId: session.staff_user_id,
          targetUserId: session.target_user_id,
          targetUserInfo: {
            email: (targetProfile as any)?.email || '',
            displayName: (targetProfile as any)?.display_name || ''
          }
        };
        
        setImpersonationState(newState);
        setHasActiveSession(true);
        
        // Store in sessionStorage for persistence
        sessionStorage.setItem('impersonation_state', JSON.stringify(newState));
      } else {
        setHasActiveSession(false);
        setImpersonationState({
          isImpersonating: false,
          staffUserId: null,
          targetUserId: null,
          targetUserInfo: null,
        });
        sessionStorage.removeItem('impersonation_state');
      }
    } catch (error) {
      console.error('Error checking database for impersonation sessions:', error);
      setHasActiveSession(false);
    }
  };

  const startImpersonation = async (targetUserId: string): Promise<{ success: boolean; error?: string; magicLink?: string }> => {
    if (!session) {
      return { success: false, error: 'No session available' };
    }

    try {
      const response = await supabase.functions.invoke('user-impersonation', {
        body: {
          action: 'start',
          targetUserId: targetUserId
        }
      });

      if (response.data?.success) {
        return {
          success: true,
          magicLink: response.data.magicLink
        };
      } else {
        return {
          success: false,
          error: response.data?.error || 'Failed to start impersonation'
        };
      }
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      return {
        success: false,
        error: 'Failed to start impersonation'
      };
    }
  };

  const getActiveMagicLink = async (): Promise<{ success: boolean; error?: string; magicLink?: string }> => {
    if (!session) {
      return { success: false, error: 'No session available' };
    }

    try {
      const response = await supabase.functions.invoke('user-impersonation', {
        body: {
          action: 'get_active_session'
        }
      });

      if (response.data?.success) {
        return {
          success: true,
          magicLink: response.data.magicLink
        };
      } else {
        return {
          success: false,
          error: response.data?.error || 'Failed to get active session'
        };
      }
    } catch (error) {
      console.error('Failed to get active session:', error);
      return {
        success: false,
        error: 'Failed to get active session'
      };
    }
  };

  const endImpersonation = async (): Promise<{ success: boolean; error?: string }> => {
    if (!session) {
      return { success: false, error: 'No session available' };
    }

    try {
      const response = await supabase.functions.invoke('user-impersonation', {
        body: {
          action: 'end'
        }
      });

      if (response.data?.success) {
        setImpersonationState({
          isImpersonating: false,
          staffUserId: null,
          targetUserId: null,
          targetUserInfo: null,
        });
        setHasActiveSession(false);
        
        // Clear sessionStorage
        sessionStorage.removeItem('impersonation_state');
        
        return { success: true };
      } else {
        return {
          success: false,
          error: response.data?.error || 'Failed to end impersonation'
        };
      }
    } catch (error) {
      console.error('Failed to end impersonation:', error);
      return {
        success: false,
        error: 'Failed to end impersonation'
      };
    }
  };

  return (
    <ImpersonationContext.Provider value={{
      impersonationState,
      hasActiveSession,
      startImpersonation,
      endImpersonation,
      getActiveMagicLink,
      checkImpersonationStatus
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
};